import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import {
  Field,
  DialogActions,
  inputCls,
  selectCls,
  textareaCls,
} from "./form-controls";

interface Contractor {
  _id: string;
  name: string;
  serviceType: string;
}

interface Unit {
  _id: string;
  label: string;
}

// Used for both tenant and landlord submissions. Tenant mode auto-
// resolves the unit from the caller's active tenancy and hides the
// scope picker; landlord mode shows a Property-wide / specific-unit
// dropdown and uses the landlord-only mutation.
export function RequestMaintenanceDialog({
  open,
  onClose,
  mode,
  propertySlug,
}: {
  open: boolean;
  onClose: () => void;
  mode: "tenant" | "landlord";
  // Required for landlord mode (the unit picker needs to know which
  // property's units to list and the mutation needs the slug).
  propertySlug?: string;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contractorId, setContractorId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [priority, setPriority] = useState<
    "low" | "normal" | "high" | "urgent"
  >("normal");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitTenant = useMutation(anyApi.maintenance.requestForUnit);
  const submitLandlord = useMutation(anyApi.maintenance.requestAsLandlord);
  const contractors = useQuery(
    anyApi.contractors.list,
    open ? {} : "skip",
  ) as Contractor[] | undefined;
  const units = useQuery(
    anyApi.units.listForProperty,
    open && mode === "landlord" && propertySlug
      ? { propertySlug }
      : "skip",
  ) as Unit[] | undefined;

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDescription("");
    setContractorId("");
    setUnitId("");
    setPriority("normal");
    setError(null);
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (mode === "landlord") {
        if (!propertySlug) {
          throw new Error("missing propertySlug for landlord submission");
        }
        await submitLandlord({
          propertySlug,
          title,
          description: description || undefined,
          unitId: unitId || undefined,
          contractorId: contractorId || undefined,
          priority,
        });
      } else {
        await submitTenant({
          title,
          description: description || undefined,
          contractorId: contractorId || undefined,
          priority,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        mode === "landlord"
          ? "Add maintenance/upgrade request"
          : "Maintenance/Upgrade request"
      }
    >
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <Field label="What's the issue or upgrade?" required>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            autoFocus
            placeholder="e.g. Bathroom sink slow drip, install ceiling fan"
            className={inputCls}
          />
        </Field>
        <Field label="Anything else helpful to know?">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="When does it happen? Anything you've already tried?"
            className={`${textareaCls} resize-none`}
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Priority">
            <select
              value={priority}
              onChange={(e) =>
                setPriority(
                  e.target.value as "low" | "normal" | "high" | "urgent",
                )
              }
              className={selectCls}
            >
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </Field>
          {mode === "landlord" && (
            <Field label="Scope">
              <select
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                className={selectCls}
              >
                <option value="">Property-wide</option>
                {(units ?? []).map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
        <Field
          label={
            mode === "landlord"
              ? "Suggest a contractor (optional)"
              : "Recommend a contractor (optional)"
          }
        >
          <select
            value={contractorId}
            onChange={(e) => setContractorId(e.target.value)}
            className={selectCls}
          >
            <option value="">No suggestion</option>
            {(contractors ?? []).map((c) => (
              <option key={c._id} value={c._id}>
                {c.name} · {c.serviceType}
              </option>
            ))}
          </select>
        </Field>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <DialogActions
          onCancel={onClose}
          submitting={submitting}
          submitLabel={mode === "landlord" ? "Add request" : "Submit request"}
          submittingLabel="Submitting..."
          disabled={!title.trim()}
        />
      </form>
    </Dialog>
  );
}
